/**
 * RevcloudPropsEditorForm — REVCLOUD-PROPS-EDITOR (fullstack §5.1).
 *
 * The FIRST real per-entity form registered on the CC-3 `SpecialEditorHost`
 * (`SPECIAL_EDITOR_REGISTRY["Revision Cloud"]`). AutoCAD surfaces a revision
 * cloud's arc/style/mode behind a dedicated properties editor
 * (`REVCLOUDPROPERTIES`) rather than the plain Properties grid, because editing
 * the arc length triggers a REGEN of the derived cloud bulges. This form drives
 * that editor's BODY; the host owns the chrome (title / OK / Cancel / Apply) and
 * the write-back → regen cycle (`useSpecialEditorRegenCommit`).
 *
 * ## Read model (ПРИОРИТЕТ #1 — projects FROM the CAD core, never re-defines it)
 *
 * Every value the form shows is read through the DEFUSE S15 typed
 * `TypedPropertyDescriptor[]` the host passes in (`descriptor.typedFieldRef.read`),
 * NOT the string-bag `EntityPreview.properties`. The four editable fields —
 *   - Cloud style        (`revisionCloudStyle`, Normal / Calligraphy),
 *   - Minimum arc length (`arcChordLength`),
 *   - Approximate arc length (`approximateArcLength` — drives the rendered bulge
 *     count: `buildRevisionCloudSceneGeometry` re-tessellates the cloud from this
 *     value every frame, so an edit is the visible "regen arcs"),
 *   - Creation mode      (`creationMode`, Rectangular / Polygonal / Object)
 * — resolve to real typed fields on `RevisionCloudEntity`.
 *
 * ## Write-back (safe contour)
 *
 * The shared `useSpecialEditorFieldDraft` controller diffs the draft against the
 * baseline and emits a single `properties/apply` carrying ONLY the changed fields
 * (property-label → value, the labels the document translator maps via
 * `labelToFieldByType["Revision Cloud"]`). The host dispatches it through the
 * store (validation → geometry reducer → audit → undo) and records the `regen`
 * view-op. The form NEVER mutates geometry directly (ПРИОРИТЕТ #1 / CLAUDE.md
 * "любые изменения геометрии только через Command Engine"). A changed arc length
 * that is ≤ 0 / non-finite ABORTS the apply (`validate` → false) so OK stays
 * open on invalid input, matching AutoCAD's reject-on-invalid.
 *
 * ## Function dependency map
 *
 * RevcloudPropsEditorForm (SpecialEditorFormComponent)
 *   props: { entity, descriptors, onDraftChange }
 *   useT()                        -> i18n.useT (RU+EN field labels + enum options)
 *   readDescriptorValue()         -> descriptor.typedFieldRef.read (typed read model)
 *   deriveBaseline()              -> the persisted draft the form resets to
 *   validate()                    -> arc-length reject rules
 *   useSpecialEditorFieldDraft()  -> shared draft controller (dirty + write-back)
 */

import { useCallback, useMemo } from "react";
import type { ReactElement } from "react";

import type { TypedPropertyDescriptor } from "@dxf-ai-editor/command-engine";
import type {
  DrawingEntity,
  RevisionCloudEntity,
} from "@dxf-ai-editor/drawing-model";

import { useT } from "../i18n";
import { formatNumber } from "../scene/draftSharedHelpers";
import type { SpecialEditorFormProps } from "./SpecialEditorHost";
import {
  SpecialEditorNumberRow,
  SpecialEditorSelectRow,
  type SpecialEditorSelectOption,
} from "./SpecialEditorFieldRows";
import {
  useSpecialEditorFieldDraft,
  type SpecialEditorDraftValues,
} from "./useSpecialEditorFieldDraft";

/* ────────────────────────── field ↔ descriptor labels ─────────────────────── */

/** The descriptor labels this form reads/writes. Each is a real
 *  `revisionCloudDescriptors` entry (DEFUSE S15) whose `typedFieldRef` resolves a
 *  `RevisionCloudEntity` field, and each is a `labelToFieldByType["Revision Cloud"]`
 *  key so the emitted `properties/apply` persists through the document translator. */
const LABEL = {
  cloudStyle: "Cloud style",
  minArc: "Minimum arc length",
  approxArc: "Approximate arc length",
  creationMode: "Creation mode",
} as const;

/** Cloud-style options — AutoCAD REVCLOUD arc styles (`revisionCloudStyle`,
 *  model default "Normal"). Reuses the existing REVCLOUD-MODES keyword labels. */
const CLOUD_STYLE_OPTIONS: ReadonlyArray<SpecialEditorSelectOption> = [
  { value: "Normal", labelKey: "keyword.revcloud.style_normal" },
  { value: "Calligraphy", labelKey: "keyword.revcloud.style_calligraphy" },
];

/** Creation-mode options — the `RevisionCloudEntity.creationMode` enum
 *  (Rectangular / Polygonal / Object; Freehand persists as Polygonal). */
const CREATION_MODE_OPTIONS: ReadonlyArray<SpecialEditorSelectOption> = [
  { value: "Rectangular", labelKey: "keyword.revcloud.rectangular" },
  { value: "Polygonal", labelKey: "keyword.revcloud.polygonal" },
  { value: "Object", labelKey: "keyword.revcloud.object" },
];

/* ──────────────────────────────── read model ─────────────────────────────── */

/** Resolve a typed value through the S15 descriptor read model. Falls back to the
 *  typed `RevisionCloudEntity` field (NOT the string bag) if the descriptor is
 *  absent — defensive; the registered descriptors always carry these four. */
function readDescriptorValue(
  descriptors: readonly TypedPropertyDescriptor[],
  label: string,
  entity: DrawingEntity,
  fallback: () => unknown,
): unknown {
  const descriptor = descriptors.find((d) => d.label === label);
  const value = descriptor?.typedFieldRef?.read(entity);
  return value ?? fallback();
}

/** The persisted draft the form resets to — a `label → displayString` map (the
 *  shared controller shape). Numbers are `formatNumber`d so an unchanged reopen
 *  renders exactly the persisted display (no spurious dirty). */
function deriveBaseline(
  entity: DrawingEntity,
  descriptors: readonly TypedPropertyDescriptor[],
): SpecialEditorDraftValues {
  const rc = entity as RevisionCloudEntity;
  const num = (label: string, fallback: () => number): string =>
    formatNumber(
      Number(readDescriptorValue(descriptors, label, entity, fallback)),
    );
  return {
    [LABEL.cloudStyle]: String(
      readDescriptorValue(descriptors, LABEL.cloudStyle, entity, () => rc.revisionCloudStyle),
    ),
    [LABEL.minArc]: num(LABEL.minArc, () => rc.arcChordLength),
    [LABEL.approxArc]: num(LABEL.approxArc, () => rc.approximateArcLength),
    [LABEL.creationMode]: String(
      readDescriptorValue(descriptors, LABEL.creationMode, entity, () => rc.creationMode),
    ),
  };
}

/** A changed arc-length value must be a positive finite number (AutoCAD rejects
 *  ≤ 0 / non-numeric arc length). Matches the descriptor `exclusiveMin: 0`. */
function isValidArc(value: string): boolean {
  const n = Number(value.trim());
  return value.trim() !== "" && Number.isFinite(n) && n > 0;
}

/* ──────────────────────────────── component ──────────────────────────────── */

export function RevcloudPropsEditorForm({
  entity,
  descriptors,
  onDraftChange,
}: SpecialEditorFormProps): ReactElement {
  const t = useT();

  const baseline = useMemo(
    () => deriveBaseline(entity, descriptors),
    [entity, descriptors],
  );

  // Reject an invalid arc length — the host keeps OK open so the user can fix it
  // (AutoCAD parity: a special editor does not close on invalid input).
  const validate = useCallback(
    (changed: SpecialEditorDraftValues): boolean => {
      for (const arcLabel of [LABEL.minArc, LABEL.approxArc]) {
        if (arcLabel in changed && !isValidArc(changed[arcLabel]!)) return false;
      }
      return true;
    },
    [],
  );

  const { draft, update } = useSpecialEditorFieldDraft(entity, baseline, onDraftChange, validate);

  return (
    <div data-testid="revcloud-props-form">
      <SpecialEditorSelectRow
        id="revcloud-cloud-style"
        testId="revcloud-cloud-style"
        label={t("property.cloud_style")}
        value={draft[LABEL.cloudStyle]!}
        options={CLOUD_STYLE_OPTIONS}
        onChange={(v) => update({ [LABEL.cloudStyle]: v })}
      />
      <SpecialEditorNumberRow
        id="revcloud-min-arc"
        testId="revcloud-min-arc"
        label={t("property.minimum_arc_length")}
        value={draft[LABEL.minArc]!}
        min="0"
        onChange={(v) => update({ [LABEL.minArc]: v })}
      />
      <SpecialEditorNumberRow
        id="revcloud-approx-arc"
        testId="revcloud-approx-arc"
        label={t("property.approximate_arc_length")}
        value={draft[LABEL.approxArc]!}
        min="0"
        onChange={(v) => update({ [LABEL.approxArc]: v })}
      />
      <SpecialEditorSelectRow
        id="revcloud-creation-mode"
        testId="revcloud-creation-mode"
        label={t("property.creation_mode")}
        value={draft[LABEL.creationMode]!}
        options={CREATION_MODE_OPTIONS}
        onChange={(v) => update({ [LABEL.creationMode]: v })}
      />
    </div>
  );
}
